/**
 * Notification Center - Displays real-time notifications for friend requests,
 * connections, and VPN events using WebSocket updates
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocketMessage } from '../contexts/WebSocketContext';
import type {
  FriendRequestReceivedMessage,
  FriendRequestAcceptedMessage,
  SessionCreatedMessage,
  SessionUpdatedMessage,
  SessionEndedMessage,
  VPNStatusChangedMessage,
} from '../services/websocket';

interface Notification {
  id: string;
  type: 'friend_request' | 'session_created' | 'session_updated' | 'vpn_status_changed';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
}

const NotificationCenter: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Get user-specific storage key
  const getStorageKey = useCallback(() => {
    return user?.id ? `notifications_${user.id}` : 'notifications';
  }, [user?.id]);

  // Load notifications from localStorage on mount or when user changes
  useEffect(() => {
    const stored = localStorage.getItem(getStorageKey());
    if (stored) {
      try {
        const parsed = JSON.parse(stored).map((n: Notification) => ({
          ...n,
          timestamp: new Date(n.timestamp)
        }));
        setNotifications(parsed);
        setUnreadCount(parsed.filter((n: Notification) => !n.read).length);
      } catch (error) {
        console.error('Failed to parse stored notifications:', error);
      }
    } else {
      // Clear notifications if no data for this user
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [getStorageKey]);

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    if (user?.id) {
      localStorage.setItem(getStorageKey(), JSON.stringify(notifications));
      setUnreadCount(notifications.filter(n => !n.read).length);
    }
  }, [notifications, getStorageKey, user?.id]);

  // Helper to add notification (memoized to prevent re-subscriptions)
  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date(),
      read: false
    };

    setNotifications(prev => [newNotification, ...prev].slice(0, 50)); // Keep last 50
  }, []);

  // WebSocket event listeners with proper typing
  useWebSocketMessage('friend_request_received', (message) => {
    const msg = message as FriendRequestReceivedMessage;
    addNotification({
      type: 'friend_request',
      title: 'New Friend Request',
      message: `${msg.sender_name || 'Someone'} sent you a friend request`,
      actionUrl: '/friends'
    });
  });

  useWebSocketMessage('friend_request_accepted', (message) => {
    const msg = message as FriendRequestAcceptedMessage;
    addNotification({
      type: 'friend_request',
      title: 'Friend Request Accepted',
      message: `${msg.friend_name || 'Someone'} accepted your friend request`,
      actionUrl: '/friends'
    });
  });

  useWebSocketMessage('session_created', (message) => {
    const msg = message as SessionCreatedMessage;
    addNotification({
      type: 'session_created',
      title: 'New Connection Request',
      message: `${msg.requester_name || 'A friend'} wants to connect to your VPN`,
      actionUrl: '/connections'
    });
  });

  useWebSocketMessage('session_updated', (message) => {
    const msg = message as SessionUpdatedMessage;
    if (msg.status === 'approved') {
      addNotification({
        type: 'session_updated',
        title: 'Session Approved',
        message: 'Your VPN connection request was approved',
        actionUrl: '/connections'
      });
    } else if (msg.status === 'rejected') {
      addNotification({
        type: 'session_updated',
        title: 'Session Rejected',
        message: 'Your VPN connection request was rejected',
        actionUrl: '/connections'
      });
    } else if (msg.status === 'active') {
      addNotification({
        type: 'session_updated',
        title: 'Session Active',
        message: 'Your VPN connection is now active',
        actionUrl: '/connections'
      });
    }
  });

  useWebSocketMessage('session_ended', (message) => {
    const msg = message as SessionEndedMessage;
    addNotification({
      type: 'session_updated',
      title: 'Session Ended',
      message: msg.reason || 'VPN session has ended',
      actionUrl: '/connections'
    });
  });

  useWebSocketMessage('vpn_status_changed', (message) => {
    const msg = message as VPNStatusChangedMessage;
    if (msg.sharing_enabled) {
      addNotification({
        type: 'vpn_status_changed',
        title: 'VPN Sharing Enabled',
        message: 'A friend is now sharing their VPN',
        actionUrl: '/connections'
      });
    }
  });

  const markAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      setIsOpen(false);
    }
  };

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative">
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
      >
        <BellIcon className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-30"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Panel */}
          <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-[52] max-h-[600px] flex flex-col">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
              <div className="flex items-center space-x-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-primary-600 hover:text-primary-700"
                  >
                    Mark all read
                  </button>
                )}
                {notifications.length > 0 && (
                  <button
                    onClick={clearAll}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>

            {/* Notification List */}
            <div className="overflow-y-auto flex-1">
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <BellIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No notifications yet</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`px-4 py-3 hover:bg-gray-50 transition-colors duration-200 transition-colors cursor-pointer ${
                        !notification.read ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className={`text-sm font-medium text-gray-900 ${!notification.read ? 'font-semibold' : ''}`}>
                              {notification.title}
                            </p>
                            {!notification.read && (
                              <span className="inline-block w-2 h-2 bg-primary-600 rounded-full" />
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTimestamp(notification.timestamp)}
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notification.id);
                          }}
                          className="ml-2 text-gray-400 hover:text-gray-600"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationCenter;
